import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { useAuth } from "../src/auth";
import { Coin } from "../src/components/brand";
import { trpc } from "../src/trpc";
import { colors, fonts, radius, space } from "../src/theme";

// Saved runs come from every calculator now; label the product per card.
const PRODUCT_LABEL: Record<string, string> = {
  AU08: "Private health",
  AU06: "HECS / HELP",
  AU13: "Childcare",
};

// "My results" — the signed-in user's saved AU-08 runs (calc.myAssessments).
// Closes the auth loop: a run computed while logged in (or claimed after
// signing in) lands here. Anonymous visitors are nudged to /auth instead of
// hitting the protected query.

type SavedAssessment = Awaited<
  ReturnType<typeof trpc.calc.myAssessments.query>
>[number];

export default function MyResults(): JSX.Element {
  const router = useRouter();
  const { status, user, signOut } = useAuth();

  const [items, setItems] = useState<SavedAssessment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status !== "authed") return;
    let active = true;
    setItems(null);
    setError(null);
    trpc.calc.myAssessments
      .query()
      .then((res) => {
        if (active) setItems(res);
      })
      .catch((err: unknown) => {
        if (active) {
          setError(err instanceof Error ? err.message : "Request failed");
        }
      });
    return () => {
      active = false;
    };
  }, [status]);

  if (status === "loading") {
    return (
      <Centered>
        <ActivityIndicator color={colors.honey} />
      </Centered>
    );
  }

  if (status === "anon") {
    return (
      <Centered>
        <Text style={styles.title}>Sign in to see your results</Text>
        <Text style={styles.muted}>
          Saved calculations live in your free account.
        </Text>
        <PrimaryButton
          label="Sign in or create an account"
          onPress={() => router.push("/auth")}
        />
        <Pressable onPress={() => router.replace("/")} accessibilityRole="button">
          <Text style={styles.backLink}>← Back to calculator</Text>
        </Pressable>
      </Centered>
    );
  }

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.container}>
      <StatusBar style="dark" />

      <Pressable onPress={() => router.replace("/")} accessibilityRole="button">
        <Text style={styles.backLink}>← Back to calculator</Text>
      </Pressable>

      <View style={styles.header}>
        <Coin size={44} />
        <Text style={styles.title}>My results</Text>
        {user !== null ? (
          <Text style={styles.muted}>Signed in as {user.email}</Text>
        ) : null}
      </View>

      {error !== null ? (
        <View style={styles.card}>
          <Text style={styles.errorTitle}>Couldn&apos;t load your results</Text>
          <Text style={styles.muted}>{error}</Text>
        </View>
      ) : items === null ? (
        <View style={styles.loading}>
          <ActivityIndicator color={colors.honey} />
        </View>
      ) : items.length === 0 ? (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>No saved results yet</Text>
          <Text style={styles.muted}>
            Run the calculator and your result will be saved here automatically.
          </Text>
          <PrimaryButton
            label="Open the calculator"
            onPress={() => router.replace("/")}
          />
        </View>
      ) : (
        items.map((item) => (
          <Pressable
            key={item.id}
            accessibilityRole="button"
            testID="myresult-card"
            onPress={() => router.push({ pathname: item.route, params: { id: item.id } })}
            style={styles.resultCard}
          >
            <View style={styles.resultTop}>
              <Text style={styles.resultDate}>{formatDate(item.createdAt)}</Text>
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{PRODUCT_LABEL[item.product]}</Text>
              </View>
            </View>
            <Text style={styles.resultHeadline}>{item.title}</Text>
            <Text style={styles.resultSub}>{item.subtitle}</Text>
            <Text style={styles.resultLink}>View result →</Text>
          </Pressable>
        ))
      )}

      <Pressable
        onPress={() => void signOut()}
        accessibilityRole="button"
        testID="signout"
        style={styles.signOut}
      >
        <Text style={styles.signOutText}>Sign out</Text>
      </Pressable>
    </ScrollView>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function Centered({ children }: { children: ReactNode }): JSX.Element {
  return (
    <View style={styles.centered}>
      <StatusBar style="auto" />
      {children}
    </View>
  );
}

function PrimaryButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}): JSX.Element {
  return (
    <Pressable onPress={onPress} accessibilityRole="button" style={styles.primary}>
      <Text style={styles.primaryText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  container: {
    gap: space.lg,
    padding: space.xl,
    paddingBottom: space.xxl,
    maxWidth: 560,
    width: "100%",
    alignSelf: "center",
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: space.md,
    padding: space.xl,
    backgroundColor: colors.cream,
  },
  loading: { paddingVertical: space.xxl, alignItems: "center" },
  header: { gap: space.sm },
  title: { fontFamily: fonts.display, fontSize: 28, letterSpacing: -0.8, color: colors.ink },
  muted: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  backLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },

  card: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    gap: space.sm,
    backgroundColor: colors.card,
  },
  cardTitle: { fontFamily: fonts.display, fontSize: 17, color: colors.ink },
  errorTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.danger },

  resultCard: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    gap: space.xs,
    backgroundColor: colors.card,
  },
  resultTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultDate: {
    fontFamily: fonts.mono,
    fontSize: 11,
    color: colors.muted,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  badge: {
    backgroundColor: colors.honeyTint,
    borderRadius: radius.pill,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  badgeText: { fontFamily: fonts.bodySemibold, fontSize: 11, color: colors.honeyDeep },
  resultHeadline: { fontFamily: fonts.bodyBold, fontSize: 16, color: colors.ink },
  resultSub: { fontFamily: fonts.body, fontSize: 14, color: colors.inkSoft },
  resultLink: {
    fontFamily: fonts.bodySemibold,
    fontSize: 14,
    color: colors.honeyDeep,
    marginTop: space.xs,
  },

  primary: {
    backgroundColor: colors.honey,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.xl,
    marginTop: space.sm,
    alignItems: "center",
  },
  primaryText: { fontFamily: fonts.bodyBold, color: colors.ink, fontSize: 15 },

  signOut: {
    marginTop: space.lg,
    paddingVertical: space.md,
    alignItems: "center",
  },
  signOutText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.inkSoft },
});
